import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Stack, CircularProgress } from '@mui/material';
import { CommentSection, PageContainer } from '../../../components';
import { useFirmDetail } from '../../../hooks/useFirmDetail';
import { useCanView, SCREENS } from '../../../hooks';
import FirmHero from './components/FirmHero';
import FirmStats from './components/FirmStats';
import FirmTabsPanel from './components/FirmTabsPanel';
import FirmInfoCard from './components/FirmInfoCard';
import CollectionCard from './components/CollectionCard';

const FirmDetailPage = () => {
  const { firmId } = useParams();
  const { t } = useTranslation();
  const { firm, offers, invoices, loading, accessDenied } = useFirmDetail(firmId);
  const [activeTab, setActiveTab] = useState(0);
  // Mali bilgiler (CollectionCard, FirmStats'taki totalRevenue) sadece
  // INVOICE_MANAGEMENT VIEW yetkisi olanlara görünür. Bu sayede teknik
  // sorumlu rolündeki kullanıcılar firma detayında "alacak/verecek"i
  // göremez.
  const canSeeFinancial = useCanView(SCREENS.INVOICE_MANAGEMENT);

  const contracts = firm?.contracts || [];
  const jobs = firm?.jobs || [];
  const totalRevenue = invoices.reduce((sum, inv) => sum + (parseFloat(inv.total_unit_price) || 0), 0);
  const firmLabel = firm?.firm_short_name || firm?.firm_name || '?';

  if (loading) {
    return (
      <PageContainer currentPage="...">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 10 }}>
          <CircularProgress />
        </Box>
      </PageContainer>
    );
  }

  if (accessDenied) {
    return (
      <PageContainer currentPage={t('errors.unauthorized.title', 'Yetkisiz Erişim')}>
        <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', py: 10, gap: 2 }}>
          <Typography variant="h6" color="text.secondary">{t('errors.unauthorized.title', 'Yetkisiz Erişim')}</Typography>
          <Typography color="text.secondary">{t('errors.unauthorized.message', 'Bu firmaya erişim yetkiniz bulunmamaktadır.')}</Typography>
        </Box>
      </PageContainer>
    );
  }

  if (!firm) {
    return (
      <PageContainer currentPage={t('firmDetail.notFound')}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 10 }}>
          <Typography color="text.secondary">{t('firmDetail.notFound')}</Typography>
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer currentPage={firmLabel}>
      <Box sx={{ py: 3, px: 2 }}>
        <FirmHero firm={firm} />

        <FirmStats
          contracts={contracts}
          offers={offers}
          invoices={invoices}
          jobs={jobs}
          totalRevenue={totalRevenue}
          showFinancial={canSeeFinancial}
        />

        <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
          <Box sx={{ flex: 3, minWidth: 0 }}>
            <Stack gap={2.5}>
              <FirmTabsPanel
                contracts={contracts}
                offers={offers}
                jobs={jobs}
                activeTab={activeTab}
                onTabChange={setActiveTab}
              />
              {canSeeFinancial && (
                <CollectionCard firmId={parseInt(firmId)} firm={firm} />
              )}
            </Stack>
          </Box>

          <Box sx={{ flex: 1.5, minWidth: 0 }}>
            <Stack gap={2.5}>
              <FirmInfoCard firm={firm} contracts={contracts} offers={offers} />
              <Box>
                <Typography variant="subtitle2" fontWeight={700} mb={1} color="text.secondary" px={0.25}>
                  {t('comments.title')}
                </Typography>
                <CommentSection entityType="firm" entityId={parseInt(firmId)} sx={{ height: 360 }} />
              </Box>
            </Stack>
          </Box>
        </Box>
      </Box>
    </PageContainer>
  );
};

export default FirmDetailPage;
